import { HttpApiError, HttpApiGroup } from "@effect/platform";
import { Schema } from "effect";
import { endpoint } from "../auth/endpoint";
import { DatabaseError, DataIntegrityError } from "../auth/errors";
import { MessageId, SupportTicketId } from "../ids/definitions";
import { MongoCustomerId, MongoUserId } from "../ids/mongo-ids";
import {
  VoiceDirection,
  VoiceStatus,
} from "../models/shopper-ai/conversation/message/channel-data";

/**
 * Optional payload for `POST /api/voice/telephony-token`. When
 * `targetMongoUserId` is supplied AND the caller is a Redo employee
 * AND the target user is on the same team, the backend mints the JWT
 * for that user instead of the authenticated caller. Used by the dev
 * test harness to spin up multiple agent panels acting as different
 * team members from one browser tab. Otherwise the caller's own
 * credential is used.
 */
export const GetTelnyxTelephonyTokenInput = Schema.Struct({
  targetMongoUserId: Schema.optional(MongoUserId),
});
export type GetTelnyxTelephonyTokenInput =
  typeof GetTelnyxTelephonyTokenInput.Type;

/**
 * Response shape for `POST /api/voice/telephony-token`. The `token` is a
 * short-lived JWT issued by Telnyx and passed directly to the WebRTC
 * client as `login_token`. The client is responsible for refreshing the
 * token before it expires.
 */
export const GetTelnyxTelephonyTokenResponse = Schema.Struct({
  token: Schema.String,
});
export type GetTelnyxTelephonyTokenResponse =
  typeof GetTelnyxTelephonyTokenResponse.Type;

/**
 * Input for `POST /api/voice/prepareOutboundCall`. Called by the agent
 * frontend before placing an outbound call. The backend creates the
 * v2 `call` row and returns the encoded client_state the frontend
 * attaches to the Telnyx WebRTC INVITE.
 */
export const PrepareOutboundCallInput = Schema.Struct({
  customerPhoneNumber: Schema.String,
  mongoCustomerId: Schema.optional(MongoCustomerId),
});
export type PrepareOutboundCallInput = typeof PrepareOutboundCallInput.Type;

export const PrepareOutboundCallResponse = Schema.Struct({
  messageId: MessageId,
  clientState: Schema.String,
  fromPhoneNumber: Schema.String,
  toPhoneNumber: Schema.String,
});
export type PrepareOutboundCallResponse =
  typeof PrepareOutboundCallResponse.Type;

/**
 * Mirrors `UserCallAvailability` in the legacy voice model. Kept as a
 * literal here so the wire schema doesn't depend on the redo-model
 * package — only the three states the inbound flow's
 * `areAnyAgentsActive` check looks for.
 */
export const VoiceAvailabilityStatus = Schema.Literal(
  "available",
  "in-call",
  "unavailable",
);
export type VoiceAvailabilityStatus = typeof VoiceAvailabilityStatus.Type;

export const SetVoiceAvailabilityInput = Schema.Struct({
  status: VoiceAvailabilityStatus,
  /**
   * Same impersonation hook as `getTelnyxTelephonyToken`: only honored
   * when the caller is a Redo employee acting on a team member.
   */
  targetMongoUserId: Schema.optional(MongoUserId),
});
export type SetVoiceAvailabilityInput = typeof SetVoiceAvailabilityInput.Type;

/**
 * One waiting customer in the team's voice queue. Returned from
 * `getVoiceQueue`. `enqueuedAt` lets the dev UI render wait time.
 */
export const VoiceQueueEntryView = Schema.Struct({
  messageId: MessageId,
  customerCallControlId: Schema.String,
  customerNumber: Schema.String,
  merchantNumber: Schema.String,
  priority: Schema.Number,
  enqueuedAt: Schema.Date,
  lastRingAttemptedAt: Schema.NullOr(Schema.Date),
});
export type VoiceQueueEntryView = typeof VoiceQueueEntryView.Type;

export const GetVoiceQueueResponse = Schema.Struct({
  entries: Schema.Array(VoiceQueueEntryView),
});
export type GetVoiceQueueResponse = typeof GetVoiceQueueResponse.Type;

/**
 * One agent's voice presence row for the current team. Returned from
 * `getVoiceCallAvailability` for the dev UI.
 */
export const VoiceCallAvailabilityView = Schema.Struct({
  mongoUserId: MongoUserId,
  status: Schema.String,
  updatedAt: Schema.NullOr(Schema.Date),
});
export type VoiceCallAvailabilityView = typeof VoiceCallAvailabilityView.Type;

export const GetVoiceCallAvailabilityResponse = Schema.Struct({
  agents: Schema.Array(VoiceCallAvailabilityView),
});
export type GetVoiceCallAvailabilityResponse =
  typeof GetVoiceCallAvailabilityResponse.Type;

/**
 * One active ring leg — an agent currently being dialed for a queued
 * customer call. Returned from `getActiveRings` for the dev UI.
 */
export const ActiveRingView = Schema.Struct({
  messageId: MessageId,
  mongoUserId: MongoUserId,
  agentCallControlId: Schema.String,
  customerNumber: Schema.String,
  merchantNumber: Schema.String,
  rungAt: Schema.Date,
});
export type ActiveRingView = typeof ActiveRingView.Type;

export const GetActiveRingsResponse = Schema.Struct({
  rings: Schema.Array(ActiveRingView),
});
export type GetActiveRingsResponse = typeof GetActiveRingsResponse.Type;

/**
 * One recent call for the team — surfaced on the v2 dev tab so the
 * operator can confirm calls are arriving and that support tickets are
 * being created. The `supportTicketId` is null until the hangup handler
 * (or a backfill) materializes the v2 support ticket/thread/message.
 */
export const RecentCallView = Schema.Struct({
  messageId: MessageId,
  supportTicketId: Schema.NullOr(SupportTicketId),
  direction: VoiceDirection,
  status: VoiceStatus,
  customerNumber: Schema.String,
  merchantNumber: Schema.String,
  startedAt: Schema.Date,
  endedAt: Schema.NullOr(Schema.Date),
  durationSeconds: Schema.NullOr(Schema.Number),
  recordingUrl: Schema.NullOr(Schema.String),
  customerMongoId: Schema.NullOr(MongoCustomerId),
});
export type RecentCallView = typeof RecentCallView.Type;

export const GetRecentCallsResponse = Schema.Struct({
  calls: Schema.Array(RecentCallView),
});
export type GetRecentCallsResponse = typeof GetRecentCallsResponse.Type;

/**
 * Input for `POST /api/voice/hold`. Toggles a v2 call between hold (Telnyx
 * `playback_start` with the team's hold music) and resume
 * (`playback_stop`). Mirrors v1's `toggleCallOnHold` but keyed by v2
 * `messageId` instead of the v1 `customerCallId`.
 */
export const SetCallHoldInput = Schema.Struct({
  messageId: MessageId,
  onHold: Schema.Boolean,
});
export type SetCallHoldInput = typeof SetCallHoldInput.Type;

/**
 * Input for `POST /api/voice/recording`. Toggles call recording via Telnyx
 * `record_start` / `record_stop`. Mirrors v1's `toggleCallBeingRecorded`
 * but keyed by v2 `messageId`.
 */
export const SetCallRecordingInput = Schema.Struct({
  messageId: MessageId,
  recording: Schema.Boolean,
});
export type SetCallRecordingInput = typeof SetCallRecordingInput.Type;

/**
 * Input for `POST /api/voice/lookup-customer-by-phone`. Pure read — does NOT
 * create a customer if none exists (the v1 RPC's `getOrCreate` behavior was
 * wrong for an inbound-display lookup, since ringing calls that drop
 * shouldn't leave dangling customer rows).
 */
export const LookupCustomerByPhoneInput = Schema.Struct({
  phoneNumber: Schema.String,
});
export type LookupCustomerByPhoneInput = typeof LookupCustomerByPhoneInput.Type;

export const LookupCustomerByPhoneCustomer = Schema.Struct({
  mongoCustomerId: MongoCustomerId,
  name: Schema.String,
  imageUrl: Schema.NullOr(Schema.String),
});
export type LookupCustomerByPhoneCustomer =
  typeof LookupCustomerByPhoneCustomer.Type;

export const LookupCustomerByPhoneResponse = Schema.Struct({
  customer: Schema.NullOr(LookupCustomerByPhoneCustomer),
});
export type LookupCustomerByPhoneResponse =
  typeof LookupCustomerByPhoneResponse.Type;

/**
 * Input for `POST /api/voice/add-agent-to-call`. Used to warm-transfer
 * an in-progress call to another agent. The caller MUST currently be a
 * participant on `messageId`; the backend dials `targetMongoUserId` via
 * the same SIP credential path used for ring-team, but with a
 * `warm_transfer_response` client state so the answered handler joins
 * the new leg into the existing conference instead of running first-
 * pickup logic.
 */
export const AddAgentToCallInput = Schema.Struct({
  messageId: MessageId,
  targetMongoUserId: MongoUserId,
});
export type AddAgentToCallInput = typeof AddAgentToCallInput.Type;

/**
 * Dev/diagnostic endpoint for the Voice v2 test harness. Dials an
 * agent's per-user SIP credential directly, without requiring a
 * queued/customer call.
 */
export const DebugSipPingAgentInput = Schema.Struct({
  targetMongoUserId: MongoUserId,
});
export type DebugSipPingAgentInput = typeof DebugSipPingAgentInput.Type;

export const DebugSipPingAgentResponse = Schema.Struct({
  agentCallControlId: Schema.String,
  sipUsername: Schema.String,
});
export type DebugSipPingAgentResponse = typeof DebugSipPingAgentResponse.Type;

/**
 * Input for `POST /api/voice/add-phone-number-to-call`. Same as
 * `addAgentToCall` but the dialed party is an arbitrary external phone
 * number (no `mongo_user_id`). Insert a `voice_participant` row keyed
 * on the new leg's `agent_call_control_id` so the participants list +
 * hangup handler can resolve it later.
 */
export const AddPhoneNumberToCallInput = Schema.Struct({
  messageId: MessageId,
  phoneNumber: Schema.String,
});
export type AddPhoneNumberToCallInput = typeof AddPhoneNumberToCallInput.Type;

/**
 * One participant currently on a v2 voice call. The frontend renders
 * these as avatar+name rows in the active-call section. Discriminated
 * by `kind`:
 *  - `customer` — the original caller / dialed party (one per call)
 *  - `agent`    — a Redo team member who answered or was warm-transferred
 *  - `external` — an arbitrary phone number warm-transferred in
 *
 * `callControlId` is stable per leg and used as the React key.
 */
export const ConferenceParticipantView = Schema.Union(
  Schema.Struct({
    kind: Schema.Literal("customer"),
    callControlId: Schema.String,
    name: Schema.String,
    phoneNumber: Schema.String,
  }),
  Schema.Struct({
    kind: Schema.Literal("agent"),
    callControlId: Schema.String,
    mongoUserId: MongoUserId,
    name: Schema.String,
  }),
  Schema.Struct({
    kind: Schema.Literal("external"),
    callControlId: Schema.String,
    phoneNumber: Schema.String,
  }),
  // The user's own forwarded phone leg (when they've saved a
  // forwardingPhoneNumber). The frontend uses the matching mongoUserId
  // to mute + deafen its WebRTC session locally so the user only hears
  // audio on the phone.
  Schema.Struct({
    kind: Schema.Literal("forwarded_phone"),
    callControlId: Schema.String,
    mongoUserId: MongoUserId,
    phoneNumber: Schema.String,
  }),
);
export type ConferenceParticipantView = typeof ConferenceParticipantView.Type;

export const GetConferenceParticipantsInput = Schema.Struct({
  messageId: MessageId,
});
export type GetConferenceParticipantsInput =
  typeof GetConferenceParticipantsInput.Type;

export const GetConferenceParticipantsResponse = Schema.Struct({
  participants: Schema.Array(ConferenceParticipantView),
  // The support ticket the call belongs to. Inbound calls
  // materialize this when the call hits the team's support number;
  // outbound calls when the agent dials. The frontend uses this to
  // surface a "Go to ticket" button while the call is active.
  supportTicketId: Schema.NullOr(SupportTicketId),
});
export type GetConferenceParticipantsResponse =
  typeof GetConferenceParticipantsResponse.Type;

/**
 * Historical participant view — used by the dev test harness to inspect
 * who was on a call (current OR past). Unlike `ConferenceParticipantView`
 * (which is filtered to the live Telnyx conference), this includes every
 * tracked leg with its joined/left timestamps so completed calls show
 * their full participant list.
 */
export const CallParticipantHistoryView = Schema.Union(
  Schema.Struct({
    kind: Schema.Literal("customer"),
    callControlId: Schema.String,
    name: Schema.String,
    phoneNumber: Schema.String,
  }),
  Schema.Struct({
    kind: Schema.Literal("agent"),
    callControlId: Schema.NullOr(Schema.String),
    mongoUserId: MongoUserId,
    name: Schema.String,
    rangAt: Schema.NullOr(Schema.Date),
    answeredAt: Schema.NullOr(Schema.Date),
    leftAt: Schema.NullOr(Schema.Date),
  }),
  Schema.Struct({
    kind: Schema.Literal("external"),
    callControlId: Schema.NullOr(Schema.String),
    phoneNumber: Schema.String,
    rangAt: Schema.NullOr(Schema.Date),
    answeredAt: Schema.NullOr(Schema.Date),
    leftAt: Schema.NullOr(Schema.Date),
  }),
);
export type CallParticipantHistoryView = typeof CallParticipantHistoryView.Type;

export const GetCallParticipantsInput = Schema.Struct({ messageId: MessageId });
export type GetCallParticipantsInput = typeof GetCallParticipantsInput.Type;

export const GetCallParticipantsResponse = Schema.Struct({
  participants: Schema.Array(CallParticipantHistoryView),
});
export type GetCallParticipantsResponse =
  typeof GetCallParticipantsResponse.Type;

/**
 * Response for `POST /api/voice/get-my-forwarding-number`. The
 * authenticated user's saved forwarding phone number, or `null` if
 * none is set. Stored on `user.voiceMetadata.forwardingPhoneNumber`.
 */
export const GetMyVoiceForwardingNumberResponse = Schema.Struct({
  forwardingPhoneNumber: Schema.NullOr(Schema.String),
});
export type GetMyVoiceForwardingNumberResponse =
  typeof GetMyVoiceForwardingNumberResponse.Type;

/**
 * Input for `POST /api/voice/set-my-forwarding-number`. `null` clears
 * the saved forwarding number. The handler validates a basic E.164-ish
 * shape (`+` followed by 8–17 digits) before persisting.
 */
export const SetMyVoiceForwardingNumberInput = Schema.Struct({
  forwardingPhoneNumber: Schema.NullOr(Schema.String),
});
export type SetMyVoiceForwardingNumberInput =
  typeof SetMyVoiceForwardingNumberInput.Type;

/**
 * Input for `POST /api/voice/set-enabled`. Enables or disables inbound
 * voice for the team by setting/clearing `deleted_at` on the team's
 * `shopper_ai_telnyx_voice_integration` row. When disabled, the inbound
 * webhook can't resolve a voice integration for the DID and hangs up
 * without processing the call. Has no effect when no voice integration
 * has been provisioned yet.
 */
export const SetVoiceEnabledInput = Schema.Struct({ enabled: Schema.Boolean });
export type SetVoiceEnabledInput = typeof SetVoiceEnabledInput.Type;

/**
 * One speaker-labelled segment from an AWS Transcribe JSON output. The raw
 * Transcribe response uses snake_case; this schema normalises to camelCase at
 * the API boundary so consumers don't need to know the AWS wire format.
 */
export const TranscriptAudioSegment = Schema.Struct({
  speakerLabel: Schema.String,
  transcript: Schema.String,
});
export type TranscriptAudioSegment = typeof TranscriptAudioSegment.Type;

/**
 * Input for `POST /api/voice/transcript`. Identifies the voice message whose
 * transcript should be returned.
 */
export const GetVoiceTranscriptInput = Schema.Struct({ messageId: MessageId });
export type GetVoiceTranscriptInput = typeof GetVoiceTranscriptInput.Type;

/**
 * Response for `POST /api/voice/transcript`. `audioSegments` is `null` when
 * no transcript has been generated yet (recording exists but transcription job
 * hasn't finished). Each segment comes from an AWS Transcribe
 * `audio_segments` item and carries the speaker label + spoken text.
 */
export const GetVoiceTranscriptResponse = Schema.Struct({
  audioSegments: Schema.NullOr(Schema.Array(TranscriptAudioSegment)),
});
export type GetVoiceTranscriptResponse = typeof GetVoiceTranscriptResponse.Type;

/**
 * Voice RPC group — v2 only. V1 has its own `getTelnyxTelephonyToken` RPC
 * in merchant/server that mints a JWT from the team-wide credential; this
 * group is the v2 replacement that mints a per-user credential on demand.
 * `prepareOutboundCall` is the only remaining call-preparation endpoint:
 * incoming calls now arrive via SIP INVITE directly and are accepted via
 * `Call.answer()` in the SDK, so `prepareAcceptCall` was removed.
 */
export const VoiceGroup = HttpApiGroup.make("voice")
  .add(
    endpoint.merchant
      .post("getTelnyxTelephonyToken", "/voice/telephony-token")
      .setPayload(GetTelnyxTelephonyTokenInput)
      .addSuccess(GetTelnyxTelephonyTokenResponse)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.InternalServerError)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("prepareOutboundCall", "/voice/prepare-outbound-call")
      .setPayload(PrepareOutboundCallInput)
      .addSuccess(PrepareOutboundCallResponse)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.InternalServerError)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("setVoiceAvailability", "/voice/availability")
      .setPayload(SetVoiceAvailabilityInput)
      .addSuccess(Schema.Void)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.InternalServerError)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("getVoiceQueue", "/voice/queue")
      .addSuccess(GetVoiceQueueResponse)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.InternalServerError)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("getVoiceCallAvailability", "/voice/call-availability")
      .addSuccess(GetVoiceCallAvailabilityResponse)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.InternalServerError)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("getActiveRings", "/voice/active-rings")
      .addSuccess(GetActiveRingsResponse)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.InternalServerError)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("getRecentCalls", "/voice/recent-calls")
      .addSuccess(GetRecentCallsResponse)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.InternalServerError)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("setCallHold", "/voice/hold")
      .setPayload(SetCallHoldInput)
      .addSuccess(Schema.Void)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.InternalServerError)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("setCallRecording", "/voice/recording")
      .setPayload(SetCallRecordingInput)
      .addSuccess(Schema.Void)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.InternalServerError)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("lookupCustomerByPhone", "/voice/lookup-customer-by-phone")
      .setPayload(LookupCustomerByPhoneInput)
      .addSuccess(LookupCustomerByPhoneResponse)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.InternalServerError)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("addAgentToCall", "/voice/add-agent-to-call")
      .setPayload(AddAgentToCallInput)
      .addSuccess(Schema.Void)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.InternalServerError)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("debugSipPingAgent", "/voice/debug/sip-ping-agent")
      .setPayload(DebugSipPingAgentInput)
      .addSuccess(DebugSipPingAgentResponse)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.InternalServerError)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("addPhoneNumberToCall", "/voice/add-phone-number-to-call")
      .setPayload(AddPhoneNumberToCallInput)
      .addSuccess(Schema.Void)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.InternalServerError)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("getConferenceParticipants", "/voice/conference-participants")
      .setPayload(GetConferenceParticipantsInput)
      .addSuccess(GetConferenceParticipantsResponse)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.InternalServerError)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("getCallParticipants", "/voice/call-participants")
      .setPayload(GetCallParticipantsInput)
      .addSuccess(GetCallParticipantsResponse)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.InternalServerError)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("getMyVoiceForwardingNumber", "/voice/get-my-forwarding-number")
      .addSuccess(GetMyVoiceForwardingNumberResponse)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.InternalServerError)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("setMyVoiceForwardingNumber", "/voice/set-my-forwarding-number")
      .setPayload(SetMyVoiceForwardingNumberInput)
      .addSuccess(Schema.Void)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.InternalServerError)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("setVoiceEnabled", "/voice/set-enabled")
      .setPayload(SetVoiceEnabledInput)
      .addSuccess(Schema.Void)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.InternalServerError)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("getVoiceTranscript", "/voice/transcript")
      .setPayload(GetVoiceTranscriptInput)
      .addSuccess(GetVoiceTranscriptResponse)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.InternalServerError)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .prefix("/api");
